<template>
  <Dialog :common-dialog="dialog" :dialog-width="dialogWidth">
    <template v-slot:title>
      <div class="d-flex align-center justify-space-between w-100">
        <div class="d-flex align-center">
          <v-avatar size="38" color="cyan lighten-5" class="mr-3">
            <v-icon color="cyan darken-2">mdi-home-map-marker</v-icon>
          </v-avatar>
          <div class="dialog-title color-custom-blue">Select Address</div>
        </div>
        <v-btn
          icon
          small
          class="custom-grey-border bg-slate-50"
          v-on:click="$emit('close', true)"
        >
          <v-icon color="slate-600" small>mdi-close</v-icon>
        </v-btn>
      </div>
    </template>
    <template v-slot:body>
      <div class="pa-2 w-100">
        <!-- Search Toolbar -->
        <v-card flat outlined class="rounded-lg bg-slate-50 px-3 py-2 mb-3">
          <v-row dense align="center">
            <v-col cols="12" class="py-0 mb-1">
              <label
                for="supplier-address-search"
                class="font-size-13 font-weight-600 mb-0"
              >
                Which address would you like to select for this supplier?
              </label>
            </v-col>
            <v-col cols="12" sm="8" md="8">
              <v-text-field
                id="supplier-address-search"
                v-model="search"
                placeholder="Search Address..."
                dense
                filled
                solo
                flat
                hide-details
                color="cyan"
                prepend-inner-icon="mdi-magnify"
                clearable
                :loading="loading"
                :disabled="loading"
                @keydown.enter="getAddress()"
                class="custom-erp-input bg-white rounded-lg"
              />
            </v-col>
            <v-col cols="12" sm="4" md="4" class="d-flex align-center">
              <v-btn
                v-on:click="getAddress()"
                :loading="loading"
                :disabled="loading"
                depressed
                color="cyan darken-2 white--text"
                class="custom-bold-button w-100"
                height="36"
              >
                <v-icon small left>mdi-magnify</v-icon>
                Search
              </v-btn>
            </v-col>
          </v-row>
        </v-card>

        <!-- Address List Container -->
        <perfect-scrollbar
          :options="{ suppressScrollX: true }"
          class="scroll pr-1"
          style="max-height: calc(100vh - 320px); position: relative"
        >
          <div v-if="list && list.length">
            <v-card
              v-for="(row, index) in list"
              :key="`supplier-address-list-key-${index}`"
              flat
              outlined
              class="rounded-lg px-3 py-0 mb-2 bg-white hover-shadow cursor-pointer transition-fast"
              v-on:click="selectAddress(row)"
            >
              <v-row dense align="center">
                <!-- Left: Address Info -->
                <v-col cols="12" md="8" class="py-1">
                  <div
                    class="d-flex align-center flex-wrap gap-2 mb-1"
                    v-if="row.barcode"
                  >
                    <Barcode :barcode="row.barcode" />
                  </div>

                  <div
                    class="font-weight-700 font-size-14 color-custom-blue mb-1 d-flex align-center custom-nowrap-ellipsis"
                  >
                    <v-icon small left color="slate-600">mdi-map-marker</v-icon>
                    <span v-if="row?.property_name" class="mr-1"
                      >{{ row.property_name }} -
                    </span>
                    <span>{{ row.property_address || "-" }}</span>
                  </div>
                </v-col>

                <!-- Right: Select CTA -->
                <v-col
                  cols="12"
                  md="4"
                  class="text-md-right d-flex flex-column align-md-end justify-center py-1"
                >
                  <v-btn
                    small
                    depressed
                    color="cyan lighten-5"
                    class="custom-bold-button px-3 cyan--text text-darken-2"
                  >
                    Select Address
                    <v-icon small right>mdi-arrow-right</v-icon>
                  </v-btn>
                </v-col>
              </v-row>
            </v-card>
          </div>

          <!-- Empty State -->
          <div
            v-else
            class="d-flex flex-column align-center justify-center py-10 px-4 text-center border rounded-lg bg-slate-50 border-dashed"
          >
            <v-avatar size="56" color="grey lighten-4" class="mb-3">
              <v-icon color="slate-400" large
                >mdi-map-marker-off-outline</v-icon
              >
            </v-avatar>
            <div class="font-weight-700 font-size-15 text-slate-800 mb-1">
              No Addresses Found
            </div>
            <div class="caption-1 text-muted max-w-300">
              There are no addresses for this supplier at the moment.
            </div>
          </div>
        </perfect-scrollbar>
      </div>
    </template>
  </Dialog>
</template>

<script>
import Dialog from "@/view/pages/partials/Dialog.vue";
import Barcode from "@/view/pages/partials/Barcode.vue";
import { QUERY } from "@/core/services/store/request.module";
import ObjectPath from "object-path";

export default {
  name: "supplier-address-select",
  props: {
    dialog: {
      type: Boolean,
      default: false,
    },
    supplierId: {
      type: [Number, String],
      default: 0,
    },
  },
  data() {
    return {
      loading: false,
      search: null,
      list: [],
    };
  },
  watch: {
    dialog(param) {
      this.search = null;
      if (param) {
        this.getAddress();
      }
    },
  },
  components: {
    Dialog,
    Barcode,
  },
  methods: {
    selectAddress(row) {
      this.$emit("select", row);
      this.$emit("close", true);
      this.loading = false;
      this.search = null;
      this.list = [];
    },
    getAddress() {
      if (!this.supplierId) {
        this.list = [];
        return;
      }
      this.loading = true;
      this.list = [];
      this.$store
        .dispatch(QUERY, {
          url: `supplier-property-list/${this.supplierId}`,
          data: { search: this.search },
        })
        .then((response) => {
          this.list = ObjectPath.get(response, "data", []);
        })
        .catch((error) => {
          console.log(error);
        })
        .finally(() => {
          this.loading = false;
        });
    },
  },
  computed: {
    dialogWidth() {
      return this.lodash.toSafeInteger((document.body.clientWidth / 100) * 40);
    },
  },
};
</script>
